import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Search,
  BookOpen,
  ArrowRightLeft,
  KeyRound,
  ArrowRight,
  Clock,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { HeirShell } from '@/components/HeirShell';
import { Button } from '@/components/ui/button';
import { NIP41_CONTESTATION_WINDOW_DAYS } from '@/lib/nip41';

export default function Index() {
  useSeoMeta({
    title: 'Heir — NIP-41 Key Migration Companion',
    description: 'Pre-position successor keys, inspect migration state, and execute break-glass NIP-41 migrations on Nostr.',
  });

  return (
    <HeirShell>
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="text-center py-12 sm:py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <KeyRound className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Heir
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-4">
            NIP-41 Key Migration Companion for Nostr
          </p>
          <p className="text-sm text-slate-600 max-w-xl mx-auto mb-8">
            Pre-position a successor key while your identity is still trusted. 
            Inspect any npub's migration state. Execute break-glass recovery if a key is compromised.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <Link to="/learn">
              <Button className="bg-cyan-600 hover:bg-cyan-500 text-white px-6">
                <BookOpen className="w-4 h-4 mr-2" />
                Learn NIP-41
              </Button>
            </Link>
            <Link to="/inspect">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-6">
                <Search className="w-4 h-4 mr-2" />
                Inspect Npub
              </Button>
            </Link>
          </div>
        </div>

        {/* Concept Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <ConceptCard
            icon={BookOpen}
            title="Learn"
            description="Interactive walkthrough of how NIP-41 key migration works, what migrates, and what doesn't."
            to="/learn"
            color="cyan"
          />
          <ConceptCard
            icon={Search}
            title="Inspect"
            description="Query any npub for its NIP-41 state. See the chain, check the contestation window, validate events."
            to="/inspect"
            color="cyan"
          />
          <ComingSoonCard
            icon={Shield}
            title="Arm"
            description="Connect your active key and pre-position a successor. Publish a signed whitelist + OpenTimestamps proof."
          />
        </div>

        {/* How it Works */}
        <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 sm:p-10 mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">How NIP-41 Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <StepCard
              number={1}
              icon={Shield}
              title="Arm"
              description="While your key is healthy, publish a whitelist event naming a successor. Timestamp it on Bitcoin."
            />
            <StepCard
              number={2}
              icon={AlertTriangle}
              title="Wait (if needed)"
              description="If your key leaks, the successor key publishes a migration declaration. A contestation window begins."
            />
            <StepCard
              number={3}
              icon={CheckCircle}
              title="Settle"
              description={`After ${NIP41_CONTESTATION_WINDOW_DAYS} days with no valid challenge, NIP-41-aware clients update followers to the new key.`}
            />
          </div>
        </div>

        {/* Important Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span>What does NOT migrate</span>
            </h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start space-x-2">
                <span className="text-red-400 mt-1">×</span>
                <span>Old posts, threads, and replies</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-400 mt-1">×</span>
                <span>Reactions and zaps received</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-400 mt-1">×</span>
                <span>DM history</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-400 mt-1">×</span>
                <span>Badges, awards, app data</span>
              </li>
            </ul>
            <p className="mt-4 text-sm text-slate-500">
              Only <em>contact lists</em> on supported clients are updated. The new key starts empty.
            </p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              <span>The contestation window</span>
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              Publishing a migration does not immediately move followers. There is a{' '}
              <strong className="text-amber-400">{NIP41_CONTESTATION_WINDOW_DAYS}-day window</strong>{' '}
              during which a competing migration can be published.
            </p>
            <p className="text-sm text-slate-400 mb-4">
              If an attacker steals your key and publishes a fake migration, you can override it 
              by publishing from your real successor. The migration with the older whitelist timestamp wins.
            </p>
            <div className="bg-amber-950/20 border border-amber-500/20 rounded-lg p-3">
              <p className="text-xs text-amber-300">
                This is why arming <em>before</em> compromise matters. A pre-compromise whitelist 
                always beats a post-compromise one.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pb-12">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to secure your identity?</h2>
          <p className="text-slate-400 mb-6">Arm a successor key now, while your current key is still trusted.</p>
          <div className="inline-flex items-center space-x-2 px-6 py-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-500">
            <Shield className="w-5 h-5" />
            <span className="text-lg font-medium">Arm a Successor</span>
            <span className="ml-2 text-xs uppercase tracking-wider bg-slate-800 px-2 py-1 rounded">Coming Soon</span>
          </div>
        </div>
      </div>
    </HeirShell>
  );
}

function ConceptCard({
  icon: Icon,
  title,
  description,
  to,
  color,
}: {
  icon: any;
  title: string;
  description: string;
  to: string;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="group block bg-slate-950 border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/30 hover:bg-cyan-950/10 transition-all"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors">
          <Icon className="w-6 h-6 text-cyan-400" />
        </div>
        <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
        {title}
      </h3>
      <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
    </Link>
  );
}

function ComingSoonCard({
  icon: Icon,
  title,
  description,
}: {
  icon: any;
  title: string;
  description: string;
}) {
  return (
    <div className="block bg-slate-950 border border-slate-800 rounded-2xl p-6 cursor-default">
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
          <Icon className="w-6 h-6 text-cyan-400" />
        </div>
        <span className="text-[10px] uppercase tracking-wider bg-slate-800 text-slate-400 px-2 py-1 rounded border border-slate-700">
          Coming Soon
        </span>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({
  number,
  icon: Icon,
  title,
  description,
}: {
  number: number;
  icon: any;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
        <Icon className="w-5 h-5 text-cyan-400" />
      </div>
      <div className="text-xs font-medium text-cyan-400 uppercase tracking-wider mb-2">
        Step {number}
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
    </div>
  );
}
